import { getRouteApi, useMatch } from "@tanstack/react-router"

import { normalizeProjectKey } from "@/lib/std.ts"
import { useCurrentProject } from "@/routes/projects/projectHooks.ts"

import type { AiAgentTaskDetails, BatchInfo, ProjectKey, TID } from "@/lib/client/generated/TypeDefinitions.ts"


export function useTasksQueryFilters(batchInfo?: BatchInfo, principalId?: string | null): {
  queryKey: unknown[]
  exact: boolean
} {
  const project = useCurrentProject()

  return batchInfo
    ? {
      queryKey: ["projects", project.key.key, "tasks", "user", principalId, batchInfo.offset],
      exact: true
    }
    : {
      queryKey: ["projects", project.key.key, "tasks", "user", principalId],
      exact: false
    }
}

export function tasksByRepoKey(projectKey: ProjectKey): { queryKey: unknown[] } {
  return {
    queryKey: ["tasks", normalizeProjectKey(projectKey)],
  }
}

export function tasksByUserKey(user: TID): { queryKey: unknown[] } {
  return {
    queryKey: ["tasks", user],
  }
}

export function taskDetailsKey(taskId: string): { queryKey: unknown[] } {
  return {
    queryKey: ["taskDetails", taskId],
  }
}

export function taskKey(taskId: string): { queryKey: unknown[] } {
  return {
    queryKey: ["task", taskId],
  }
}

export function useTasksRoute() {
  return getRouteApi("/$orgId/p/$projectKey/t/")
}

export function useTaskRoute() {
  return getRouteApi("/$orgId/p/$projectKey/t/$taskId")
}

export function useNewTaskRoute() {
  return getRouteApi("/$orgId/p/$projectKey/t/new")
}

export function useProfileRoute() {
  return getRouteApi("/$orgId/user/$username")
}

export function useProfileMatch(shouldThrow = false) {
  return useMatch({
    from: "/$orgId/user/$username",
    shouldThrow: shouldThrow,
  })
}

export function useNewProjectMatch(shouldThrow = false) {
  return useMatch({
    from: "/$orgId/p/new",
    shouldThrow: shouldThrow,
  })
}

export function useTaskMatch(shouldThrow = false) {
  return useMatch({
    from: "/$orgId/p/$projectKey/t/$taskId",
    shouldThrow: shouldThrow,
  })
}

export function useNewTaskMatch(shouldThrow = false) {
  return useMatch({
    from: "/$orgId/p/$projectKey/t/new",
    shouldThrow: shouldThrow,
  })
}

export function useCurrentTask() {
  // Loader data becomes undefined because of the onLeave handler in TasksRouteViewTask
  const loaderData = useTaskRoute().useLoaderData() as {
    taskWithDetails: AiAgentTaskDetails
  }

  return loaderData.taskWithDetails
}
